// Moon.tsx — A small moon on a Kepler orbit around its parent planet (the parent
// group is centred on the planet, so the moon tracks it). Always rendered at TRUE
// scale (no apparent-size floor) so a moon reads honestly against its planet in
// planet view; at system zoom that makes it vanishingly small, which is intentional
// (moons are a planet-view detail). Each frame it publishes its own world sphere (so
// the planet can be eclipsed by it) and is itself eclipsed by the parent planet's
// sphere (analytic shadow, see shadow.ts).
import { useEffect, useMemo, useRef, useState } from 'react';
import { gameClock } from '../state/gameClock';
import { useFrame, useThree } from '@react-three/fiber';
import { Vector3 } from 'three';
import type { Group, WebGLProgramParametersWithUniforms } from 'three';
import type { ThreeEvent } from '@react-three/fiber';
import type { Moon as MoonData } from '../types';
import { useGame } from '../state/context';
import { E2E_ENABLED, e2eRegisterBody } from '../e2e';
import { orbitPosition } from './orbit';
import { apparentScale, FLOOR_NONE } from './apparentScale';
import { makeShadowUniforms, setOccluders, standardShadowPatch } from './shadow';
import type { Occluder, ShadowUniforms } from './shadow';
import { FBM3D, SIMPLEX3D } from './planet/glslNoise';
import { Bracket } from './Bracket';
import { EntityLabel } from './EntityLabel';

// The eclipse-shadow patch plus a rocky surface: broad maria/highland patches with
// finer cratering, so the moon reads as a body, not a flat-shaded gem. Composes on
// top of standardShadowPatch (which already wired the world-pos varying + shadow).
function moonSurfacePatch(shadow: ShadowUniforms) {
  const base = standardShadowPatch(shadow);
  return (shader: WebGLProgramParametersWithUniforms) => {
    base(shader);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vMoonDir;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vMoonDir = normalize(position);');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nvarying vec3 vMoonDir;\n${SIMPLEX3D}\n${FBM3D}`)
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
        {
          vec3 md = normalize(vMoonDir);
          float maria = fbm(md * 2.3);
          float craters = fbm(md * 9.0 + 5.0);
          diffuseColor.rgb *= mix(0.55, 1.15, maria) * (0.82 + 0.18 * craters);
        }`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
        roughnessFactor = clamp(roughnessFactor * (0.85 + 0.3 * fbm(normalize(vMoonDir) * 4.0)), 0.6, 1.0);`,
      );
  };
}

interface Props {
  moon: MoonData;
  /** Display name (e.g. "Sol IIIa"); shown as the moon's label in planet view. */
  name: string;
  /** Icosphere subdivisions — higher for the focused planet's moons (LOD). */
  detail: number;
  /** The parent planet is the focused one (planet view) — moons are visible and
   *  selectable then; at system zoom they're sub-pixel and stay unlabeled. */
  planetFocused: boolean;
  sunWorldPos: Vector3;
  sunRadius: number;
  /** The parent planet's sphere — casts a shadow on this moon. */
  occluder: Occluder;
  /** This moon writes its own world sphere here, for the planet's eclipse test. */
  slot: Occluder;
}

export function Moon({ moon, name, detail, planetFocused, sunWorldPos, sunRadius, occluder, slot }: Props) {
  const { focus, hover, hoveredId, focusedMoonId } = useGame();
  const [hovering, setHovering] = useState(false);
  const ref = useRef<Group>(null);
  const bodyRef = useRef<Group>(null);
  const camera = useThree((state) => state.camera);
  const scratch = useMemo(() => new Vector3(), []);
  // Always true scale — a moon's size reads honestly against its planet (tiny at
  // system zoom by design).
  const floor = FLOOR_NONE;
  const moonFocused = focusedMoonId === moon.id;
  const hovered = hovering || hoveredId === moon.id;
  const shadow = useMemo(makeShadowUniforms, []);
  const onBeforeCompile = useMemo(() => moonSurfacePatch(shadow), [shadow]);
  const occList = useMemo(() => [occluder], [occluder]);
  // The focused moon fills the screen, so build it at a higher subdivision.
  const meshDetail = moonFocused ? Math.max(detail, 5) : detail;

  // Under ?e2e=1, expose this moon's orbiting group so the playtest can read its
  // live on-screen position (it orbits its planet, which orbits the star). Inert in
  // production.
  useEffect(() => {
    if (!E2E_ENABLED) {
      return;
    }
    e2eRegisterBody(moon.id, ref.current);
    return () => e2eRegisterBody(moon.id, null);
  }, [moon.id]);

  useFrame((_state, delta) => {
    const g = ref.current;
    if (!g) {
      return;
    }
    orbitPosition(moon, gameClock.time, g.position);
    g.getWorldPosition(scratch);
    slot.pos.copy(scratch);
    slot.radius = moon.radius;
    setOccluders(shadow, sunWorldPos, sunRadius, occList);
    if (bodyRef.current) {
      bodyRef.current.scale.setScalar(apparentScale(camera, scratch, moon.radius, floor));
      if (moon.tidallyLocked) {
        bodyRef.current.rotation.y = Math.atan2(g.position.x, g.position.z);
      } else {
        bodyRef.current.rotation.y += delta * 0.3 * gameClock.speed;
      }
    }
  });

  const onClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    focus(moon.id);
  };
  const onOver = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    setHovering(true);
    hover(moon.id);
    document.body.style.cursor = 'pointer';
  };
  const onOut = () => {
    setHovering(false);
    hover(null);
    document.body.style.cursor = 'auto';
  };

  return (
    <group rotation={[moon.inclination, 0, 0]}>
      <group ref={ref}>
        <group ref={bodyRef}>
          <mesh onClick={onClick} onPointerOver={onOver} onPointerOut={onOut}>
            <icosahedronGeometry args={[moon.radius, meshDetail]} />
            <meshStandardMaterial
              color={moon.color}
              roughness={0.95}
              metalness={0.02}
              onBeforeCompile={onBeforeCompile}
            />
          </mesh>
        </group>
        {/* Selectable in planet view: bracket on hover, name label above. */}
        {hovered && planetFocused && !moonFocused ? <Bracket /> : null}
        {planetFocused ? <EntityLabel text={name} lit={hovered || moonFocused} /> : null}
      </group>
    </group>
  );
}
